import { PartnerBriefing } from '@/components/briefing/PartnerBriefing'
import { PageSeo } from '@/components/seo/PageSeo'
import { ButtonLink } from '@/components/ui/Button'
import { sitePages } from '@/config/pages'

/**
 * Private partner briefing. Deliberately unlisted: no nav or footer entry, no sitemap entry,
 * and `noindex` so it stays out of search even if the URL leaks (see src/config/pages.ts).
 */

export const FirstTwoMonthsPage = () => (
  <>
    <PageSeo page={sitePages.firstTwoMonths} />

    <PartnerBriefing
      titleId="partner-briefing-title"
      eyebrow="YOU + UNITED"
      title={sitePages.firstTwoMonths.title}
      intro={[
        'We might spend the next ten years building together. No review, no deck, and no dinner can tell either of us what that will actually be like.',
        'Real work can. So we are offering you a partnership test-drive to see what working together would look like on real clients, with real technology. You will see Justine in action, we will learn how your firm really works, and together we will turn that experience into product decisions.',
      ]}
      stagesTitle="The next steps in three stages"
      stages={[
        {
          number: '01',
          timing: 'Now',
          title: 'Work side by side',
          body: 'Run a real close. Understand the firm. Make product decisions together. This is the Design Partnership, and it is the focus of this page.',
        },
        {
          number: '02',
          timing: 'Next',
          title: 'Prepare to join',
          body: 'We finish the due diligence, most of which has already happened along the way. Then together we agree on the deal and plan the transition.',
        },
        {
          number: '03',
          timing: 'After closing',
          title: 'Transform and build together',
          body: 'Once the firm joins United, we combine its judgment and client relationships with United’s product, engineering, and capital. To build something great together',
        },
      ]}
      approachEyebrow="WHY WE WORK THIS WAY"
      approachTitle="The best accounting software will be build together with the best accounting firms."
      approachBody={[
        'Every accounting firm works a little differently. Underneath those differences are problems every accountant recognizes: collecting, checking, chasing, reviewing, and making judgment calls when the standard process is not enough.',
        'United brings firm owners who understand those problems into direct work with leading engineers and product people. Partners are not simply early users. You are in the room while the product is built. Your judgment, context, and imagination shape how it works. United brings those ideas to life.',
      ]}
      workItems={[
        {
          number: '01',
          title: 'Run a real close',
          outcome: 'The outcome: you see Justine against reality, not a demonstration.',
          points: [
            { label: 'You bring', body: 'one or two QuickBooks Online clients with an unfinished monthly close.' },
            { label: 'We do together', body: 'complete the close on Justine, including real exceptions and real judgment.' },
            { label: 'Then', body: 'review it honestly. What worked, what was missed, and what your team would have done differently.' },
            { label: 'Your time', body: 'roughly 60 to 120 minutes.' },
          ],
        },
        {
          number: '02',
          title: 'Understand your firm',
          outcome: 'The outcome: a shared picture of how your firm really runs, to make sure Justine works for you, the way you want it to.',
          points: [
            { label: 'Owner session', body: 'about 60 minutes with Peter on bottlenecks, priorities, and what you have already tried. Peter solved bookkeeping automation once before, at a company where 200 bookkeepers served 15,000 clients. This is him learning your firm, not pitching you.' },
            { label: 'Firm sessions', body: 'two or three 60-minute conversations with you or your operations lead.' },
            { label: 'What we cover', body: 'roles, workload, handoffs, client mix, service lines, exceptions, and what "done" means in your firm.' },
          ],
        },
        {
          number: '03',
          title: 'Build conviction in the partnership',
          outcome: 'The outcome: both sides believe the outcomes ahead are possible for your firm, and that we are the right partners to achieve them.',
          points: [
            { label: 'You work directly with', body: 'Peter and Karl, helping shape what gets built and how it should work.' },
            { label: 'We decide together', body: 'which problems matter, how the product should respond, and where accountants must remain in control.' },
            { label: 'Both sides learn', body: 'what it feels like to make decisions together and turn accounting judgment into product.' },
            { label: 'The rhythm', body: 'usually one to three hours in active weeks. Some weeks, none.' },
          ],
        },
      ]}
      outcomesEyebrow="UNITED. BEFORE AND AFTER"
      outcomesTitle="The outcomes."
      outcomes={[
        {
          number: '01',
          title: 'Human-led',
          body: 'Agents handle production and back-office. Accountants provide judgment, approval, and relationships.',
        },
        {
          number: '02',
          title: 'Scalable',
          body: 'Revenue grows faster than headcount. Capacity and margins expand dramatically.',
        },
        {
          number: '03',
          title: 'Financial platform',
          body: 'Every accounting client becomes a relationship through which the firm can deliver a much broader set of financial products.',
        },
      ]}
      calculator={{
        title: 'Put your own numbers against the idea.',
        body: 'Use your client count and average fee to explore how the economics could change.',
        action: (
          <ButtonLink to={sitePages.unitEconomics.path} variant="ink">
            Run your numbers
          </ButtonLink>
        ),
      }}
      closingEyebrow="AT THE END OF THIS STAGE"
      closingTitle="We should both be excited about building together"
      closingItems={[
        {
          lead: 'Justine has completed a real close',
          detail: 'on your clients, inside your firm.',
        },
        {
          lead: 'We have a shared transformation plan',
          detail: 'for how your firm gets from where it is today to the outcomes we believe are possible.',
        },
        {
          lead: 'You know the people behind United',
          detail: 'and what it actually feels like to work together.',
        },
        {
          lead: 'You know what being a United partner means',
          detail: '— your role, what changes, and what we would build together.',
        },
      ]}
      closingBody="Then, if we are both as excited at the end as we are now, we prepare to join: structure, value, and the closing plan."
    />
  </>
)
